import {
  Button,
  Typography,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText,
} from "@mui/material";
import styles from "./styles.module.scss";
import { Controller, ControllerRenderProps, useForm } from "react-hook-form";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useState } from "react";
import { textFieldStyles } from "./theme";

interface ResetPasswordStep3Props {
  password: string;
  confirmPassword: string;
}

interface Props {
  setStep: (step: number) => void;
  setIsAllFieldsReady: (isAllFieldsReady: boolean) => void;
  setData: (data: any) => void;
}

export const ResetPasswordStep3 = ({ setStep, setIsAllFieldsReady, setData }: Props) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ResetPasswordStep3Props>({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    mode: "onTouched",
  });

  const onSubmit = (data: ResetPasswordStep3Props) => {
    setIsAllFieldsReady(true);
    setData((prev: any) => ({
      ...prev,
      password: data.password,
    }));
  };

  return (
    <>
      <div className={styles.formTitleContainer}>
        <Typography className={styles.formTitle}>Новый пароль</Typography>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <Controller
          name='password'
          control={control}
          rules={{
            required: "Поле обязательно для заполнения",
            minLength: {
              value: 8,
              message: "Пароль должен быть не менее 8 символов",
            },
            validate: {
              hasLetter: (value) => /[A-Za-z]/.test(value) || "Добавьте хотя бы одну букву",
              hasNumber: (value) => /\d/.test(value) || "Добавьте хотя бы одну цифру",
              hasSpecialChar: (value) =>
                /[@$!%*#?&]/.test(value) || "Добавьте хотя бы один специальный символ (@$!%*#?&)",
              matchesPassword: (value) => value === control._formValues.password || "Пароли не совпадают",
            },
          }}
          render={({ field }: { field: ControllerRenderProps<ResetPasswordStep3Props, "password"> }) => (
            <FormControl className={styles.emailInput} sx={textFieldStyles} variant='outlined' fullWidth>
              <InputLabel htmlFor='outlined-adornment-password' sx={{ color: "#ffffff83" }}>
                Password
              </InputLabel>
              <OutlinedInput
                {...field}
                sx={{
                  borderRadius: 24,
                  color: "#fff",
                }}
                error={!!errors.password}
                type={showPassword ? "text" : "password"}
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton
                      sx={{ color: "#fff", mr: 1 }}
                      onClick={() => setShowPassword((show) => !show)}
                      edge='end'
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label='Password'
              />
              <FormHelperText error={!!errors.password}>{errors.password?.message}</FormHelperText>
            </FormControl>
          )}
        />
        <Controller
          name='confirmPassword'
          control={control}
          rules={{
            required: "Поле обязательно для заполнения",
            minLength: {
              value: 8,
              message: "Пароль должен быть не менее 8 символов",
            },
            validate: {
              hasLetter: (value) => /[A-Za-z]/.test(value) || "Добавьте хотя бы одну букву",
              hasNumber: (value) => /\d/.test(value) || "Добавьте хотя бы одну цифру",
              hasSpecialChar: (value) =>
                /[@$!%*#?&]/.test(value) || "Добавьте хотя бы один специальный символ (@$!%*#?&)",
              matchesPassword: (value) => value === control._formValues.password || "Пароли не совпадают",
            },
          }}
          render={({ field }: { field: ControllerRenderProps<ResetPasswordStep3Props, "confirmPassword"> }) => (
            <FormControl className={styles.emailInput} sx={textFieldStyles} variant='outlined' fullWidth>
              <InputLabel htmlFor='outlined-adornment-password' sx={{ color: "#ffffff83" }}>
                Password
              </InputLabel>
              <OutlinedInput
                {...field}
                sx={{
                  borderRadius: 24,
                  color: "#fff",
                }}
                error={!!errors.confirmPassword}
                type={showConfirmPassword ? "text" : "password"}
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton
                      sx={{ color: "#fff", mr: 1 }}
                      onClick={() => setShowConfirmPassword((show) => !show)}
                      edge='end'
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label='Password'
              />
              <FormHelperText error={!!errors.confirmPassword}>{errors.confirmPassword?.message}</FormHelperText>
            </FormControl>
          )}
        />

        <Button
          disabled={!isValid}
          type='submit'
          variant='contained'
          className={styles.button}
          sx={{
            textTransform: "none",
            borderRadius: 24,
            mt: 6,
            color: "#222",
            fontWeight: 600,
            background: "linear-gradient(90deg, #00FFB0 0%, #00E0FF 100%)",
            "&.Mui-disabled": {
              background: "#bfc9d1",
              color: "#00000026",
            },
          }}
        >
          Сохранить
        </Button>
      </form>
      <Button
        variant='text'
        onClick={() => setStep(2)}
        sx={{
          color: "#bfc9d1",
          fontSize: "17px",
          mt: 2,
          ml: 0,
          textAlign: "left",
          p: 0,
          minWidth: 0,
          alignSelf: "flex-start",
          textTransform: "none",
          "&:hover": {
            color: "#fff",
            background: "none",
          },
        }}
      >
        Вернуться
      </Button>
    </>
  );
};
